import React, { useState } from 'react';
import { fmtLineItemType, fmtUnit, fmtQty, fmtCurrency } from '../../lib/display-labels';
import { fmtLineMoney, isBelowZero } from '../../lib/ticket-discount';
import { getJobTypeLabel } from '../../constants/job-types';
import type { TicketStampDisplay } from '../../lib/approval-stamp';
import type { StampSignatureImage } from '../../hooks/useStampSignatureUrl';

/**
 * A LINE BELOW ZERO IS A DISCOUNT ON THE PAGE: labelled "Discount" and written
 * "-$256.47", never "$-256.47". Decided by the total alone, so a typed negative
 * rate on an ordinary line reads the same way, and a line at zero or above
 * takes the old path untouched — `money` is fmtCurrency for it, exactly.
 *
 * Never any pay wording: this component is what the customer is shown on the
 * hand's phone, and the description is the only words a discount carries.
 */
function lineView(li: { type?: string; amount?: number | string | null; total?: number | string | null }) {
  const amount = li.amount ?? li.total ?? null;
  const discount = isBelowZero(amount);
  return {
    amount,
    typeLabel: discount ? 'Discount' : fmtLineItemType(li.type || ''),
    money: discount ? fmtLineMoney : fmtCurrency,
  };
}

export interface FieldTicketDisplayProps {
  ticketNumber: string;
  status?: string;
  companyName: string;
  logoUrl?: string;
  clientName?: string;
  customerName: string;
  jobType?: string;
  rigName?: string;
  metresRun?: number | string;
  casingSize?: string;
  connectionType?: string;
  casingGrade?: string;
  workDate: string;
  startTime?: string;
  endTime?: string;
  totalHours?: string | number;
  lineItems: Array<{
    type?: string;
    description: string;
    qty?: number | string | null;
    quantity?: number | string | null;
    unit: string;
    rate: number | string | null;
    amount?: number | string | null;
    total?: number | string | null;
  }>;
  subtotal: number | string | null;
  gstAmount?: number | string | null;
  taxRate1Label?: string;
  taxRate1Amount?: number | string | null;
  taxRate2Label?: string;
  taxRate2Amount?: number | string | null;
  total: number | string | null;
  currency?: string;
  customerNotes?: string;
  customerContactName?: string;
  customerContactPhone?: string;
  crew?: { name: string; role: string }[];
  /**
   * The customer's signed approval stamp, rendered as the last section of the
   * card. Omit it and the card renders exactly as before — every surface that
   * does not pass it is unchanged.
   */
  stamp?: TicketStampDisplay | null;
  /**
   * The customer's ink signature for the stamp's signature row, from
   * `useStampSignatureUrl`. Without it — or while `src` is null — that row
   * prints its text ("Signed"), exactly as before.
   */
  stampSignature?: StampSignatureImage;
}

const FieldTicketDisplay: React.FC<FieldTicketDisplayProps> = ({
  ticketNumber,
  status,
  companyName,
  logoUrl,
  clientName,
  customerName,
  jobType,
  rigName,
  metresRun,
  casingSize,
  connectionType,
  casingGrade,
  workDate,
  startTime,
  endTime,
  totalHours,
  lineItems,
  subtotal,
  gstAmount,
  taxRate1Label,
  taxRate1Amount,
  taxRate2Label,
  taxRate2Amount,
  total,
  currency,
  customerNotes,
  customerContactName,
  customerContactPhone,
  crew,
  stamp,
  stampSignature,
}) => {
  return (
    <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
      {/* Header */}
      <div className="px-5 pt-5 pb-3 sm:px-6">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            {logoUrl && (
              <img
                src={logoUrl}
                alt={companyName}
                className="h-10 sm:h-12 w-auto object-contain flex-shrink-0"
              />
            )}
            <div className="min-w-0">
              <h2 className="text-lg sm:text-xl font-bold text-[#1e3a8a]">FIELD TICKET</h2>
              <p className="text-sm sm:text-base font-semibold text-amber-600">{ticketNumber}</p>
            </div>
          </div>
          <div className="text-right flex-shrink-0">
            <p className="font-bold text-gray-900 text-sm sm:text-base">{companyName}</p>
            <p className="text-xs sm:text-sm text-gray-500">{workDate}</p>
            {status && (
              <span className="inline-block mt-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-blue-100 text-[#1e3a8a] uppercase">
                {status}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Orange divider */}
      <div className="h-1 bg-amber-500" />

      {/* Job Information + Details two-column row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 px-5 py-4 sm:px-6">
        <div data-testid="ticket-job-information">
          <h3 className="text-xs font-bold text-amber-600 uppercase tracking-wide mb-2">Job Information</h3>
          <dl className="space-y-1.5 text-sm">
            <Row label="Customer" value={customerName} />
            {clientName && clientName !== customerName && <Row label="Client" value={clientName} />}
            {rigName && <Row label="Rig" value={rigName} />}
            {jobType && <Row label="Job Type" value={getJobTypeLabel(jobType)} />}
            {customerContactName && (
              <Row label="Contact" value={`${customerContactName}${customerContactPhone ? ` \u00b7 ${customerContactPhone}` : ''}`} />
            )}
          </dl>

          {/* Crew, inside Job Information rather than in a strip of its own.
              Job Information is the shorter of the two columns on almost every
              ticket — the Details column carries up to seven rows — so the crew
              fills space that was empty and the card loses a section break.
              Same markup as before: a label/value pair per hand, absent role
              falling back to a positional label rather than to nothing. */}
          {crew && crew.length > 0 && (
            <div className="mt-4" data-testid="ticket-crew">
              <h3 className="text-xs font-bold text-amber-600 uppercase tracking-wide mb-2">Crew</h3>
              <div className="space-y-1 text-sm">
                {crew.map((member, i) => (
                  <div key={i} className="flex gap-2">
                    <dt className="text-gray-500 flex-shrink-0 w-24">{member.role || `Tech ${i + 1}`}</dt>
                    <dd className="font-medium text-gray-900">{member.name}</dd>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <div data-testid="ticket-details">
          <h3 className="text-xs font-bold text-amber-600 uppercase tracking-wide mb-2">Details</h3>
          <dl className="space-y-1.5 text-sm">
            {casingSize && <Row label="Casing Size" value={casingSize} />}
            {connectionType && <Row label="Connection" value={connectionType} />}
            {casingGrade && <Row label="Grade" value={casingGrade} />}
            {metresRun && <Row label="Metres Run" value={fmtQty(metresRun)} />}
            {totalHours && <Row label="Hours" value={String(totalHours)} />}
            {workDate && <Row label="Date" value={workDate} />}
            {startTime && endTime && <Row label="Time" value={`${startTime} \u2013 ${endTime}`} />}
          </dl>
        </div>
      </div>

      {/* Line Items */}
      {lineItems.length > 0 && (
        <div className="px-5 pb-4 sm:px-6">
          {/* Desktop table */}
          <div className="hidden sm:block overflow-hidden rounded-lg border border-gray-200">
            <table className="w-full text-sm table-fixed">
              <thead>
                <tr className="bg-[#1e3a8a] text-white text-xs uppercase">
                  <th className="text-left px-3 py-2 font-semibold w-[45%]">Description</th>
                  <th className="text-right px-3 py-2 font-semibold w-[12%]">Qty</th>
                  <th className="text-left px-3 py-2 font-semibold w-[15%]">Unit</th>
                  <th className="text-right px-3 py-2 font-semibold w-[13%]">Rate</th>
                  <th className="text-right px-3 py-2 font-semibold w-[15%]">Amount</th>
                </tr>
              </thead>
              <tbody>
                {lineItems.map((li, i) => {
                  // `?? null`, never `?? 0`: fmtQty renders absence as '—',
                  // and a line whose quantity nobody recorded is not a line of
                  // zero. The office editor's preview coerces to 0 before it
                  // gets here, so that document is unchanged.
                  const qty = li.qty ?? li.quantity ?? null;
                  const { amount, typeLabel, money } = lineView(li);
                  return (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-3 py-2 text-gray-900">
                        {li.description}
                        {typeLabel && typeLabel !== li.description && (
                          <span className="block text-xs text-gray-400 mt-0.5">{typeLabel}</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right text-gray-700">{fmtQty(qty)}</td>
                      <td className="px-3 py-2 text-gray-600">{fmtUnit(li.unit)}</td>
                      <td className="px-3 py-2 text-right text-gray-700">{money(li.rate)}</td>
                      <td className="px-3 py-2 text-right font-semibold text-gray-900">{money(amount)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile card layout */}
          <div className="sm:hidden space-y-2">
            {lineItems.map((li, i) => {
              const qty = li.qty ?? li.quantity ?? null;
              const { amount, typeLabel, money } = lineView(li);
              return (
                <div key={i} className="border border-gray-200 rounded-lg p-3">
                  <div className="flex justify-between items-start mb-1">
                    <div className="min-w-0 flex-1 mr-2">
                      <span className="text-sm font-medium text-gray-900">{li.description}</span>
                      {typeLabel && typeLabel !== li.description && (
                        <span className="block text-xs text-gray-400">{typeLabel}</span>
                      )}
                    </div>
                    <span className="text-sm font-bold text-gray-900 flex-shrink-0">{money(amount)}</span>
                  </div>
                  <div className="text-xs text-gray-500">
                    {fmtQty(qty)} {fmtUnit(li.unit)} &times; {money(li.rate)}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Summary rows */}
          <div className="mt-3 space-y-1">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600 font-medium">Subtotal</span>
              <span className="font-semibold text-gray-900">{fmtCurrency(subtotal)}</span>
            </div>
            {taxRate1Label && taxRate1Amount && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">{taxRate1Label}</span>
                <span className="text-gray-900">{fmtCurrency(taxRate1Amount)}</span>
              </div>
            )}
            {gstAmount && !taxRate1Label && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">GST</span>
                <span className="text-gray-900">{fmtCurrency(gstAmount)}</span>
              </div>
            )}
            {taxRate2Label && taxRate2Amount && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">{taxRate2Label}</span>
                <span className="text-gray-900">{fmtCurrency(taxRate2Amount)}</span>
              </div>
            )}
            <div className="flex justify-between pt-2 border-t border-gray-200">
              <span className="text-base font-bold text-gray-900">Total</span>
              <span className="text-lg font-bold text-[#1e3a8a]">{fmtCurrency(total)} {currency || ''}</span>
            </div>
          </div>
        </div>
      )}

      {/* Customer Notes */}
      {customerNotes && (
        <div className="px-5 pb-5 sm:px-6">
          <h3 className="text-xs font-bold text-amber-600 uppercase tracking-wide mb-1.5">Notes</h3>
          <div className="border border-gray-200 rounded-lg p-3">
            <p className="text-sm text-gray-700 whitespace-pre-line">{customerNotes}</p>
          </div>
        </div>
      )}

      {/* Approval Stamp */}
      {stamp && (
        <div className="px-5 pb-5 sm:px-6">
          <div className="border-2 border-gray-300 rounded-lg p-3" data-testid="ticket-approval-stamp">
            <h3
              className={`text-sm font-bold mb-2${stamp.operatorColor ? '' : ' text-gray-900'}`}
              style={stamp.operatorColor ? { color: stamp.operatorColor } : undefined}
            >
              {stamp.operatorName}
            </h3>
            <dl className="space-y-1.5 text-sm">
              {stamp.rows.map((row, i) =>
                row.kind === 'signature' && stampSignature ? (
                  <div key={i} className="flex justify-between items-center gap-4">
                    <dt className="font-bold text-gray-700 flex-shrink-0">{row.label}</dt>
                    <StampSignatureValue text={row.value} image={stampSignature} />
                  </div>
                ) : (
                  <div key={i} className="flex justify-between gap-4">
                    <dt className="font-bold text-gray-700 flex-shrink-0">{row.label}</dt>
                    <dd className="text-gray-900 text-right break-words min-w-0">{row.value}</dd>
                  </div>
                ),
              )}
            </dl>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * The signature row's value: the ink image when it has arrived, the row's text
 * ("Signed") when there is none or it failed, and an empty box while it loads.
 * The box is the same height in all three, so the stamp does not move when the
 * image lands.
 */
function StampSignatureValue({ text, image }: { text: string; image: StampSignatureImage }) {
  const [brokenSrc, setBrokenSrc] = useState<string | null>(null);
  const showImage = image.src !== null && image.src !== brokenSrc;
  return (
    <dd className="h-12 flex items-center justify-end min-w-0 text-gray-900" data-testid="stamp-signature-value">
      {showImage ? (
        <img
          src={image.src!}
          alt="Customer signature"
          className="max-h-12 max-w-[12rem] w-auto object-contain"
          onError={() => setBrokenSrc(image.src)}
        />
      ) : image.pending ? null : (
        text
      )}
    </dd>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex gap-2">
      <dt className="text-gray-500 flex-shrink-0 w-24">{label}</dt>
      <dd className="font-medium text-gray-900 capitalize">{value}</dd>
    </div>
  );
}

export default FieldTicketDisplay;
